import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { ArrowRight, LogOut, Crown, Medal, Trophy } from "lucide-react";

const RANK_STYLE = {
    1: { bg: "bg-gradient-to-l from-amber-400 to-yellow-500", ring: "ring-amber-300", icon: Crown, label: "المرتبة الأولى" },
    2: { bg: "bg-gradient-to-l from-slate-300 to-slate-400", ring: "ring-slate-200", icon: Medal, label: "المرتبة الثانية" },
    3: { bg: "bg-gradient-to-l from-orange-400 to-amber-600", ring: "ring-orange-200", icon: Medal, label: "المرتبة الثالثة" },
};

export default function Leaderboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [lb, setLb] = useState(null);

    useEffect(() => { api.get("/leaderboard").then((r) => setLb(r.data)).catch(() => {}); }, []);

    if (!lb) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">جاري التحميل...</div>;

    const backPath = user?.role === "parent" ? "/parent" : "/student";
    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => nav(backPath)} data-testid="btn-back" className="text-white/80 hover:text-white hover:bg-white/10"><ArrowRight className="w-4 h-4 ml-1" /> الرئيسية</Button>
                        <BrandLogo size={36} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="text-white/80 hover:text-white hover:bg-white/10"><LogOut className="w-4 h-4" /></Button>
                </div>
            </header>

            <main className="max-w-4xl mx-auto p-6 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-l from-amber-400 to-yellow-500 text-white flex items-center justify-center shadow-md"><Trophy className="w-6 h-6" /></div>
                    <div>
                        <h1 className="text-2xl font-bold text-[#0F172A]">لوحة شرف الفصل</h1>
                        <p className="text-sm text-slate-500">{lb.class?.name} — أعلى 5 طالبات هذا الأسبوع</p>
                    </div>
                </div>

                {lb.my_rank && (
                    <div data-testid="my-rank-card" className="bg-gradient-to-l from-[#1B2A4E] to-[#0F172A] text-white rounded-2xl p-5 flex items-center justify-between">
                        <div>
                            <div className="text-xs text-white/60 uppercase tracking-wider mb-1">مرتبتك الحالية</div>
                            <div className="text-3xl font-bold font-mono-num">#{lb.my_rank.rank} <span className="text-sm text-white/60 font-normal">من {lb.my_rank.total}</span></div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs text-white/60 uppercase tracking-wider mb-1">متوسطك</div>
                            <div className="text-3xl font-bold font-mono-num text-[#C8102E]">{lb.my_rank.avg_score}%</div>
                        </div>
                    </div>
                )}

                <div className="space-y-3">
                    {lb.entries.length === 0 && (
                        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
                            لا توجد نتائج بعد في هذا الفصل. ابدئي بحل الاختبارات لتظهري في لوحة الشرف!
                        </div>
                    )}
                    {lb.entries.map((e) => {
                        const style = RANK_STYLE[e.rank] || { bg: "bg-slate-100", ring: "ring-slate-200", icon: Trophy, label: `المرتبة ${e.rank}` };
                        const RankIcon = style.icon;
                        return (
                            <div
                                key={e.rank}
                                data-testid={`lb-row-${e.rank}`}
                                className={`bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4 transition-all ${e.is_me ? "ring-2 ring-[#C8102E] shadow-md" : "hover:shadow-md"}`}
                            >
                                <div className={`w-14 h-14 rounded-xl ${style.bg} text-white flex items-center justify-center shadow-md`}>
                                    <RankIcon className="w-6 h-6" />
                                </div>
                                <div className={`relative`}>
                                    <Avatar url={e.avatar_url} name={e.display_name} seed={e.display_name} role="student" size={48} className={`ring-2 ${style.ring}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-[#0F172A] flex items-center gap-2">
                                        <span className="truncate">{e.display_name}</span>
                                        {e.is_me && <span className="text-xs bg-[#C8102E] text-white rounded-full px-2 py-0.5">أنتِ</span>}
                                        {e.hidden && <span className="text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">خصوصية</span>}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">{e.attempts} اختبار</div>
                                </div>
                                <div className="text-left">
                                    <div className="text-2xl font-bold text-[#1B2A4E] font-mono-num">{e.avg_score}%</div>
                                    <div className="text-xs text-slate-500">المتوسط</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {user?.role === "student" && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
                        <strong>الخصوصية:</strong> يمكنك إخفاء اسمك من هذه اللوحة من صفحة «حسابي» — سيبقى ترتيبك مسجّلاً لكِ فقط.
                    </div>
                )}
            </main>
        </div>
    );
}
